<template>
  <ts-button variant="primary" size="md" @click="handlePDFAction">
    Download form
  </ts-button>
</template>

<script setup lang="ts">
const { PDFDocument } = await import("pdf-lib");

const authStore = useAuthStore();
const { id, first_name, last_name, mobile, username, company }: any =
  authStore.user || {};

const fieldsToFill = new Map([
  ["Tekstveld 2", company],
  ["Tekstveld 3", first_name],
  ["Tekstveld 4", last_name],
  ["Tekstveld 5", ""],
  ["Tekstveld 6", ""],
  ["Tekstveld 7", ""],
  ["Tekstveld 8", ""],
  ["Tekstveld 9", mobile],
  ["Tekstveld 10", username],
  ["Tekstveld 76", formatDate(new Date())],
]);

const fallbackPdfUrl =
  "https://cdn.toolstation.nl/content/202408301-NL-Corporate-identity-Letters_-forms_-etc.-Retourformulier.pdf";

const validatePDFHeader = (arrayBuffer: ArrayBuffer): boolean => {
  try {
    const headerBytes = new Uint8Array(arrayBuffer.slice(0, 1024)); // Check first 1KB
    const headerString = new TextDecoder().decode(headerBytes);

    // Check for PDF signature
    if (!headerString.startsWith("%PDF-")) {
      console.error("Invalid PDF header: Missing PDF signature");
      return false;
    }

    // Check for binary content marker
    if (
      headerString.slice(0, 1024).indexOf("%%EOF") === -1 &&
      headerString.length >= 1024
    ) {
      console.warn("PDF header warning: No EOF marker in first 1KB");
    }

    return true;
  } catch (error) {
    console.error("Error validating PDF header:", error);
    return false;
  }
};

const fillAndOpenPDF = async () => {
  const accountStore = useAccountStore();
  // const pdfUrl =
  //   "/pdf/202408301-NL-Corporate-identity-letters_-forms_-etc.-Retourformulier.pdf";
  const pdfUrl = "/api/pdf/download";

  try {
    const response = await fetch(pdfUrl, {
      credentials: "same-origin",
      headers: {
        Accept: "application/pdf",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get("content-type");
    if (!contentType?.includes("application/pdf")) {
      throw new Error("Invalid content type received: " + contentType);
    }

    const existingPdfBytes = await response.arrayBuffer();

    // Validate PDF header
    if (!validatePDFHeader(existingPdfBytes)) {
      throw new Error("Invalid PDF structure: Header validation failed");
    }

    // Load PDF with more options
    const pdfDoc = await PDFDocument.load(existingPdfBytes, {
      ignoreEncryption: true,
      updateMetadata: false,
      throwOnInvalidObject: false,
      parseSpeed: 50, // Slower but more reliable parsing
    });

    // Load address data
    await accountStore.getSavedAddresses(id, {
      filter: true
    });

    // Update address fields with null checks
    fieldsToFill.set("Tekstveld 5", accountStore.primaryAddress?.formatted?.[0] ?? "");
    fieldsToFill.set("Tekstveld 6", accountStore.primaryAddress?.postcode ?? "");
    fieldsToFill.set("Tekstveld 7", accountStore.primaryAddress?.town ?? "");
    fieldsToFill.set(
      "Tekstveld 8",
      accountStore.primaryAddress?.formatted
        ? accountStore.primaryAddress.formatted[
            accountStore.primaryAddress.formatted.length - 1
          ]
        : ""
    );

    const form = pdfDoc.getForm();

    // Fill form fields with better error handling
    for (const [key, value] of fieldsToFill) {
      try {
        const field = form.getTextField(key);
        if (field) {
          field.setText(value || "");
        }
      } catch (fieldError) {
        console.warn(`Failed to fill field ${key}:`, fieldError);
        // Continue with other fields even if one fails
      }
    }

    // Save with specific options
    const pdfBytes = await pdfDoc.save({
      useObjectStreams: false, // More compatible but larger file size
      addDefaultPage: false,
      updateFieldAppearances: true,
    });

    const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
    const pdfUrlNew = URL.createObjectURL(pdfBlob);

    window.open(pdfUrlNew, "_blank");
  } catch (error) {
    console.error("Error processing PDF:", error);
    if (
      error.message.includes("Header overflow") ||
      error.message.includes("Parse Error")
    ) {
      console.warn("Falling back to direct PDF download due to parsing error");
    }
    // window.location.href = fallbackPdfUrl;
  }
};

const handlePDFAction = () => {
  if (authStore.is_authenticated) {
    fillAndOpenPDF();
  } else {
    window.open(fallbackPdfUrl, "_blank");
  }

  const fileData = {
  'event': 'file_download',
  'file_extension': 'pdf',
  'file_name': 'return-exchange-form',
  'file_text': "Download form",
  'file_link': fallbackPdfUrl
}

logEvent({
      options: {
        eventAction: "click",
        eventProperties: fileData,
        eventInteraction: true,
        eventLabel: `User click to fill in Return & Exchange form ${fallbackPdfUrl}`,
        eventName: "file_download",
      },
    });
};
</script>
